import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getZoneProfile, confirmZoneProfile, refilterZoneProfile } from '../services/zoneProfileService.js';

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 9000;
  background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Montserrat', system-ui, sans-serif;
`;
const Modal = styled.div`
  width: 560px; max-width: calc(100vw - 32px); max-height: calc(100vh - 64px);
  overflow-y: auto; background: #fff; border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.2); padding: 1.5rem;
`;
const Title = styled.h2`font-size: 1.05rem; font-weight: 700; margin: 0 0 0.25rem; color: #1a1a1a;`;
const Sub = styled.p`font-size: 0.8rem; color: #6b7280; margin: 0 0 1rem; line-height: 1.5;`;
const Summary = styled.div`
  background: #f5f7ff; border: 1px solid rgba(59,130,246,0.2); border-radius: 8px;
  padding: 0.75rem 0.875rem; font-size: 0.82rem; color: #1e40af; line-height: 1.55;
  margin-bottom: 1rem;
`;
const Field = styled.div`margin-bottom: 0.85rem;`;
const Label = styled.label`
  display: block; font-size: 0.72rem; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.4px; color: #6b7280; margin-bottom: 0.3rem;
`;
const Hint = styled.span`font-weight: 400; text-transform: none; letter-spacing: 0; color: #9ca3af;`;
const Input = styled.input`
  width: 100%; padding: 0.5rem 0.625rem; border: 1px solid #e5e7eb; border-radius: 6px;
  font-size: 0.85rem; font-family: inherit; box-sizing: border-box;
  &:focus { outline: none; border-color: #e97024; }
`;
const Footer = styled.div`display: flex; justify-content: flex-end; gap: 0.625rem; margin-top: 1.25rem;`;
const Ghost = styled.button`
  padding: 0.55rem 1rem; border-radius: 8px; border: 1px solid #e5e7eb; background: #fff;
  color: #374151; font-size: 0.82rem; font-weight: 500; cursor: pointer; font-family: inherit;
  &:hover { background: #f9fafb; }
`;
const Primary = styled.button`
  padding: 0.55rem 1.1rem; border-radius: 8px; border: none; background: #e97024; color: #fff;
  font-size: 0.82rem; font-weight: 600; cursor: pointer; font-family: inherit;
  &:disabled { opacity: 0.5; cursor: default; }
  &:hover:not(:disabled) { background: #d4621a; }
`;
const Center = styled.div`padding: 2rem; text-align: center; color: #6b7280; font-size: 0.85rem;`;

const joinList = (v) => (Array.isArray(v) ? v.join(', ') : (v || ''));
const splitList = (s) => (s || '').split(',').map(x => x.trim()).filter(Boolean);

/**
 * Review/confirm the bylaw-decoded zone profile before extraction proceeds.
 * mode: 'confirm' (default — resumes paused extraction) | 'refilter' (post-extraction edit).
 */
export default function ZoneProfilePanel({ payload, userId, mode = 'confirm', onClose, onDone, onDismissStale }) {
  const { projectId, requirementId, linkId, docName } = payload || {};
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState(null);
  const [summary, setSummary]   = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const p = await getZoneProfile(projectId, requirementId, linkId);
        if (!alive) return;
        setSummary(p.interpretationSummary || '');
        setForm({
          primaryZone:          p.primaryZone || '',
          parentCategories:     joinList(p.parentCategories),
          siblingZonesExcluded: joinList(p.siblingZonesExcluded),
          exceptions:           joinList(p.exceptions),
        });
      } catch (e) {
        if (alive) {
          setError(e.message || 'Failed to load zone profile');
          setNotFound(e.status === 404);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId, requirementId, linkId]);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleConfirm = async () => {
    setSaving(true);
    setError('');
    const edits = {
      primaryZone:          form.primaryZone.trim(),
      parentCategories:     splitList(form.parentCategories),
      siblingZonesExcluded: splitList(form.siblingZonesExcluded),
      exceptions:           splitList(form.exceptions),
    };
    try {
      if (mode === 'refilter') {
        await refilterZoneProfile(projectId, requirementId, linkId, edits);
      } else {
        await confirmZoneProfile(projectId, requirementId, linkId, edits, userId);
      }
      onDone && onDone();
      onClose && onClose();
    } catch (e) {
      setError(e.message || 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Title>Confirm zoning setup</Title>
        <Sub>
          {docName ? `${docName} — ` : ''}
          {mode === 'refilter'
            ? 'Update how your zoning designation maps to this bylaw. Saving re-checks which requirements apply — no re-extraction.'
            : 'CODiii decoded how your zoning designation maps to this bylaw. Review and confirm before requirements are extracted.'}
        </Sub>

        {loading && <Center>Loading decoded zone profile…</Center>}
        {!loading && error && !form && (
          <Center>
            {notFound ? (
              <>
                This document is no longer linked to this project.
                <br /><br />
                <Ghost onClick={onDismissStale}>Dismiss reminder</Ghost>
              </>
            ) : error}
          </Center>
        )}

        {!loading && form && (
          <>
            {summary && <Summary>{summary}</Summary>}
            <Field>
              <Label>Project zone</Label>
              <Input value={form.primaryZone} onChange={update('primaryZone')} placeholder="e.g. RM" />
            </Field>
            <Field>
              <Label>Parent categories that apply <Hint>(comma-separated)</Hint></Label>
              <Input
                value={form.parentCategories}
                onChange={update('parentCategories')}
                placeholder="e.g. Residential Zone category"
              />
            </Field>
            <Field>
              <Label>Sibling zones that do NOT apply <Hint>(comma-separated)</Hint></Label>
              <Input
                value={form.siblingZonesExcluded}
                onChange={update('siblingZonesExcluded')}
                placeholder="e.g. R, RD, RS, RT"
              />
            </Field>
            <Field>
              <Label>Exception number(s) <Hint>(comma-separated)</Hint></Label>
              <Input value={form.exceptions} onChange={update('exceptions')} placeholder="e.g. 252" />
            </Field>
            {error && <Sub style={{ color: '#b91c1c' }}>{error}</Sub>}
            <Footer>
              <Ghost onClick={onClose} disabled={saving}>Cancel</Ghost>
              <Primary onClick={handleConfirm} disabled={saving}>
                {saving
                  ? 'Saving…'
                  : mode === 'refilter' ? 'Save & re-filter' : 'Confirm & start extraction'}
              </Primary>
            </Footer>
          </>
        )}
      </Modal>
    </Overlay>
  );
}
